import React, { FC, Fragment, useCallback, useState, useEffect } from 'react';
import OrdersPageActions from 'components/orders/shared/orders-page-actions.component';
import { getSupplierOrdersTemplate } from 'apollo/templates/order';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { getOrderListRowTemplate } from 'apollo/templates/order/query.graphql';
import { useLiveOrderList } from 'hooks/useLiveOrderList';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { OrderFilter, OrderTableType } from 'models/order';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import OrderTableContainer from 'components/orders/order-table-container.container';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { OrderBy, OrderDirection } from 'models/search-result';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SupplierOrders: FC = () => {
    const { t: translate } = useTranslation(['orders']);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const liveList = useLiveOrderList('supplierOrders', getOrderListRowTemplate);
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: permissions.isExternalTechnician(currentUser?.role) ? 'linkedExtTechs.dateAdded' : 'created.on',
        orderDirection: OrderDirection.desc,
    });
    const [orderFilters, setOrderFilters] = useState<OrderFilter>(
        JSON.parse(localStorage.getItem('supplier-order-list-filters') || '{}'),
    );
    const { showBoundary } = useDenthubErrorBoundary();

    const [getSupplierOrders, { loading, fetchMore, networkStatus, refetch, data }] = useLazyQuery(
        getSupplierOrdersTemplate,
        {
            context: liveList.context,
            fetchPolicy: 'cache-and-network',
            nextFetchPolicy: 'cache-first',
            pollInterval: 5 * 60 * 1000,
            notifyOnNetworkStatusChange: true,
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const orders = liveList.ordersFrom(data);
    const lastOffset = orders.length;

    useEffect(() => {
        getSupplierOrders({
            variables: {
                limit: 20,
                offset: 0,
                filters: orderFilters,
                ...orderBy,
            },
        });
    }, [orderFilters]);

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        } else {
            getSupplierOrders({
                variables: {
                    limit: 20,
                    offset: 0,
                    filters: orderFilters,
                    ...orderBy,
                },
            });
        }
    };

    const handleFilterChange = useCallback((filters: OrderFilter) => {
        setOrderFilters(filters);
    }, []);

    const setOrderOnField = async (field: string) => {
        const direction = orderBy.orderDirection === OrderDirection.asc ? OrderDirection.desc : OrderDirection.asc;
        await getSupplierOrders({
            variables: {
                filters: orderFilters,
                limit: 20,
                offset: 0,
                orderBy: field,
                orderDirection: direction,
            },
        });
        setOrderBy({
            orderBy: field,
            orderDirection: direction,
        });
    };

    const handleUpdateOrderListForArchived = (orderId: string) => liveList.removeOrders([orderId]);

    return (
        <Fragment>
            <Helmet>
                <title>{translate('orders:ORDERS')} | Denthub</title>
            </Helmet>
            <OrdersPageActions
                initFilters={orderFilters}
                onFilterChange={handleFilterChange}
                hideClinicFilter={true}
                filtersLocalStorageKey="supplier-order-list-filters"
                hideSupplierFilter={permissions.isExternalTechnician(currentUser?.role)}
            />
            {loading && networkStatus !== NetworkStatus.fetchMore && networkStatus !== NetworkStatus.poll ? (
                <DelayedLinearProgress />
            ) : (
                <Fragment>
                    {permissions.isExternalTechnician(currentUser?.role) ? (
                        <OrderTableContainer
                            setOrderOnField={setOrderOnField}
                            tableType={OrderTableType.SUPPLIER_EXTERNAL_TECHNICIAN}
                            orderBy={orderBy}
                            orders={orders}
                            hidePatientDetails={permissions.isRestrictedExternalUser(currentUser?.role)}
                            onFetchMore={handleFetchMore}
                        />
                    ) : (
                        <OrderTableContainer
                            tableType={OrderTableType.SUPPLIER}
                            orderBy={orderBy}
                            orders={orders}
                            onFetchMore={handleFetchMore}
                            handleUpdateOrderListForArchived={handleUpdateOrderListForArchived}
                        />
                    )}
                </Fragment>
            )}
        </Fragment>
    );
};

export default SupplierOrders;
